import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';

// Inline state reads rather than duck selector imports: config.duck pulls in
// several whereabouts feature ducks, so importing it from shared components
// creates module cycles that break store initialisation.

// 'COMPANY_ADMIN' is Permission.CompanyAdmin; user.models also sits inside
// the cycle described above so the enum is not imported here.
function computeMinDate(enabled: boolean, weeksLimit: number, userRole: string | undefined): Moment | null {
  if (!enabled || userRole === 'COMPANY_ADMIN') {
    return null;
  }
  return moment().startOf('isoWeek').subtract(weeksLimit, 'weeks');
}

export interface HidePastWhereaboutsCutoff {
  minDate: Moment | null;
  weeksLimit: number;
}

// For non-React code (services, thunks) holding a store state object.
export function getHidePastWhereaboutsCutoff(state: any): HidePastWhereaboutsCutoff {
  const weeksLimit = state?.config?.configEntity?.hidePastWhereaboutsWeeksLimit ?? 0;
  return {
    minDate: computeMinDate(
      !!state?.config?.configEntity?.hidePastWhereaboutsEnabled,
      weeksLimit,
      state?.auth?.currentUser?.role,
    ),
    weeksLimit,
  };
}

export function useHidePastWhereaboutsCutoff(): HidePastWhereaboutsCutoff {
  const userRole = useSelector((state: any) => state?.auth?.currentUser?.role);
  const enabled = useSelector((state: any) => !!state?.config?.configEntity?.hidePastWhereaboutsEnabled);
  const weeksLimit = useSelector((state: any) => state?.config?.configEntity?.hidePastWhereaboutsWeeksLimit ?? 0);

  const minDate = useMemo(() => computeMinDate(enabled, weeksLimit, userRole), [enabled, weeksLimit, userRole]);

  return { minDate, weeksLimit };
}

export function hiddenPastWhereaboutsMessage(weeksLimit: number): string {
  return weeksLimit > 0
    ? `Whereabouts older than ${weeksLimit} ${weeksLimit === 1 ? 'week' : 'weeks'} are hidden by your company's settings`
    : `Whereabouts from previous weeks are hidden by your company's settings`;
}
